/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaConstArrayModel,
	ISchemaConstObjectModel,
	ISelectionItem_Base,
	isSchemaConstArrayModel,
	isSchemaConstObjectModel,
	TGenericBlueprintSchema,
	TSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import { EditSelection, TEditSelectionItem } from "@adapptio/lib-components";
import { emitEvent } from "@adapptio/lib-shared";

export enum README_SELECTION_ITEM_TYPE {
	PAGE = "page"
}

export interface ISelectionItem_Page extends ISelectionItem_Base {
	type: README_SELECTION_ITEM_TYPE.PAGE;
	parentListModelId: number;
}

export type TReadmeEditSelectionItem = TEditSelectionItem | ISelectionItem_Page;

/**
 * Type guard for page selection
 */
export function isSelectionItemPage(value: TReadmeEditSelectionItem): value is ISelectionItem_Page {
	return value?.type === README_SELECTION_ITEM_TYPE.PAGE;
}

/**
 * Class to manage readme editor selections
 */
export class ReadmeEditSelection extends EditSelection<TReadmeEditSelectionItem> {
	public constructor() {
		super();
	}

	/**
	 * Deselect all items
	 */
	public deselectAll(): void {
		this.unselectAll();
		emitEvent(this.onChange);
	}

	/**
	 * Select a page
	 */
	public selectPage(
		parentListModel: ISchemaConstArrayModel<TGenericBlueprintSchema>,
		model: ISchemaConstObjectModel<TSchemaConstObjectProps>
	): boolean {
		if (!isSchemaConstArrayModel(parentListModel) || !isSchemaConstObjectModel(model)) {
			return false;
		}

		const isSelected = this.isPageSelected(model);

		// In multiselect, de-select the item, otherwise it's already selected so do nothing
		if (isSelected && this.multiSelect) {
			this.deselectPage(model);
			return true;
		} else if (isSelected) {
			return false;
		}

		const selItem: ISelectionItem_Page = {
			type: README_SELECTION_ITEM_TYPE.PAGE,
			parentListModelId: parentListModel.nodeId,
			nodeId: model.nodeId
		};

		if (this.multiSelect) {
			this.items.push(selItem);
		} else {
			this.unselectAll();
			this.items = [ selItem ];
		}

		emitEvent(this.onSelectSingle, selItem);
		emitEvent(this.onChange);

		return true;
	}

	/**
	 * Returns true if page is selected
	 */
	public isPageSelected(model: ISchemaConstObjectModel<TSchemaConstObjectProps>): boolean {
		if (!isSchemaConstObjectModel(model)) {
			return false;
		}

		const index = this.findItemIndexByNodeId(model.nodeId);
		const item = this.items[index];

		if (isSelectionItemPage(item)) {
			return item.nodeId === model.nodeId;
		}

		return false;
	}

	/**
	 * Deselect a page
	 */
	public deselectPage(model: ISchemaConstObjectModel<TSchemaConstObjectProps>): boolean {
		if (!isSchemaConstObjectModel(model)) {
			return false;
		}

		if (!this.isPageSelected(model)) {
			return false;
		}

		return this.deselectByNodeId(model.nodeId);
	}
}
